import { db } from '../firebase';
import firebase from 'firebase/app';

const SETTINGS_COLLECTION = 'app_settings';
const VERSION_DOC = 'version';

export const DEFAULT_VERSION_SETTINGS = {
  version: '1.1.1',
  forceUpdate: true,
  title: 'নতুন সংস্করণ উপলব্ধ',
  message: 'Noor অ্যাপের একটি নতুন সংস্করণ উপলব্ধ আছে। অ্যাপের সকল ফিচার নির্বিঘ্নে উপভোগ করতে এবং সেরা অভিজ্ঞতা পেতে অনুগ্রহ করে এখনই আপডেট করে নিন।',
  playStoreUrl: 'https://play.google.com/store/apps/details?id=com.rizkware.noor',
  whatsNew: [
    'নতুন ফিচার এবং উন্নত পারফরম্যান্স',
    'বাগ ফিক্স এবং অ্যাপের স্থিতিশীলতা বৃদ্ধি',
    'মসৃণ এবং দ্রুত ব্যবহারকারী অভিজ্ঞতা'
  ],
  updatedAt: null,
  updatedBy: 'Admin'
};

/**
 * Fetch current version control settings from Firestore
 */
export async function getVersionSettings() {
  try {
    const docRef = db.collection(SETTINGS_COLLECTION).doc(VERSION_DOC);
    const snap = await docRef.get();
    if (snap.exists) {
      const data = snap.data();
      return {
        ...DEFAULT_VERSION_SETTINGS,
        ...data,
        id: snap.id,
      };
    } else {
      // Document doesn't exist yet, save default and return
      await docRef.set({
        ...DEFAULT_VERSION_SETTINGS,
        updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
      });
      return { ...DEFAULT_VERSION_SETTINGS };
    }
  } catch (error) {
    console.error('[versionService] Error fetching version settings:', error);
    throw error;
  }
}

/**
 * Save / Update version settings in Firestore
 */
export async function saveVersionSettings(settings, userEmail = 'Admin') {
  try {
    const docRef = db.collection(SETTINGS_COLLECTION).doc(VERSION_DOC);
    const payload = {
      version: (settings.version || '').trim(),
      forceUpdate: settings.forceUpdate !== undefined ? !!settings.forceUpdate : true,
      title: (settings.title || '').trim(),
      message: (settings.message || '').trim(),
      playStoreUrl: (settings.playStoreUrl || '').trim() || DEFAULT_VERSION_SETTINGS.playStoreUrl,
      whatsNew: Array.isArray(settings.whatsNew)
        ? settings.whatsNew.map(s => String(s).trim()).filter(Boolean)
        : [],
      updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
      updatedBy: userEmail,
    };

    await docRef.set(payload, { merge: true });
    return payload;
  } catch (error) {
    console.error('[versionService] Error saving version settings:', error);
    throw error;
  }
}

/**
 * Real-time listener for version settings
 */
export function subscribeVersionSettings(onUpdate, onError) {
  const docRef = db.collection(SETTINGS_COLLECTION).doc(VERSION_DOC);
  return docRef.onSnapshot(
    (snap) => {
      if (snap.exists) {
        onUpdate({ ...DEFAULT_VERSION_SETTINGS, ...snap.data(), id: snap.id });
      } else {
        onUpdate({ ...DEFAULT_VERSION_SETTINGS });
      }
    },
    (err) => {
      console.error('[versionService] Snapshot listener error:', err);
      if (onError) onError(err);
    }
  );
}

/**
 * Get distribution of active app versions from users collection
 */
export async function getAppVersionStats(targetVersion = '') {
  try {
    const snapshot = await db.collection('users').get();
    const versionMap = {};
    let totalUsersWithVersion = 0;
    let matchingUsers = 0;
    let outdatedUsers = 0;

    const normalizedTarget = (targetVersion || '').toLowerCase().replace(/^v/, '').trim();

    snapshot.docs.forEach((doc) => {
      const u = doc.data();
      const rawV = u.appVersion || u.appVersionFull;
      if (rawV) {
        const vKey = rawV.split(' ')[0].toLowerCase().replace(/^v/, '').trim();
        versionMap[vKey] = (versionMap[vKey] || 0) + 1;
        totalUsersWithVersion++;

        if (normalizedTarget) {
          if (vKey === normalizedTarget) {
            matchingUsers++;
          } else {
            outdatedUsers++;
          }
        }
      }
    });

    const versionBreakdown = Object.entries(versionMap)
      .map(([version, count]) => ({
        version,
        count,
        percent: totalUsersWithVersion > 0 ? Math.round((count / totalUsersWithVersion) * 100) : 0,
      }))
      .sort((a, b) => b.count - a.count);

    return {
      totalUsers: snapshot.size,
      totalTrackedUsers: totalUsersWithVersion,
      matchingUsers,
      outdatedUsers,
      versionBreakdown,
    };
  } catch (err) {
    console.error('[versionService] Error fetching version stats:', err);
    return {
      totalUsers: 0,
      totalTrackedUsers: 0,
      matchingUsers: 0,
      outdatedUsers: 0,
      versionBreakdown: [],
    };
  }
}
